import { Bone, BufferAttribute, BufferGeometry, Color, Skeleton, SkinnedMesh, Vector4, type Matrix4, type Object3D, type ShaderMaterial } from "three";
import type { AtmosphereUniforms } from "../render/atmosphere.ts";
import { avatarMaterial } from "../render/shaders/avatar.ts";
import { toonDissolveMaterial } from "../render/shaders/toon.ts";

/**
 * Dissolve em mais famílias (F95): a peça que o próprio jogador desmonta, os drops que saem do chão e o
 * que some sem quebrar (a Cruz, a Trouxa, o cardume) se desfazem em pedacinhos em vez de sumir. O lote
 * (estruturas, drops, recursos) é folhagem instanciada com vento, e o dissolve ali seria um programa
 * novo (`USE_DISSOLVE` com `USE_WIND`), com o limiar por material e não por instância. Então a peça sai
 * do lote e é desenhada à parte como `SkinnedMesh` de um osso, com o material de dissolve do avatar:
 * o mesmo programa que a pré-compilação já aquece (`USE_DISSOLVE`, cor por vértice RGB, skinning, sem
 * normal e fora de lote). Nenhum programa novo.
 */
export const DISSOLVE = {
  /** Malhas que se desfazem ao mesmo tempo; sem vaga, a peça some como antes. */
  slots: 8,
  /**
   * Vagas guardadas para o que o próprio jogador causa (`own`, o desmonte): uma leva de drops expirando ou
   * de cardumes não as toma.
   */
  reserved: 2,
  /** Duração (ms). */
  ms: 500,
  /** Geometrias de dissolve guardadas (uma por modelo e atributo de cor); as mais antigas sem uso saem. */
  views: 48,
} as const;

/** Uma peça que se desfaz: o modelo, a matriz dele no mundo e o material do lote em que ele estava. */
export interface DissolvePart {
  readonly geometry: BufferGeometry;
  readonly matrix: Matrix4;
  /** O material do lote: os uniforms dele (o papel, a cor do dono, o contorno, a atmosfera) vêm junto. */
  readonly material: ShaderMaterial;
  /** Atributo da cor (`color`, ou `color1`/`color2` na paleta da instância). */
  readonly colorAttribute?: string;
  /** A cor da instância (o cubo dos drops, a variação dos recursos), que multiplica a do material. */
  readonly color?: Color;
  /** Cor da borda acesa (hex já na cor do jogo: `playerColor`, o próprio desmonte); sem ela, o acento do lote (o neutro). */
  readonly edge?: string;
  /** Só o pontilhado, sem a borda acesa (os drops, que saem a toda hora). */
  readonly dither?: boolean;
}

export interface DissolveStats {
  /** Peças se desfazendo agora. */
  active: number;
  /** Dissolves começados e os que ficaram sem vaga (a peça sumiu como antes). */
  started: number;
  skipped: number;
}

interface Slot {
  mesh: SkinnedMesh;
  material: ShaderMaterial;
  dissolve: { value: number };
  color: { value: Color };
  edge: { value: Vector4 };
  dither: boolean;
  start: number;
  busy: boolean;
}

/** Atributos que a geometria de dissolve divide com o modelo (o resto do modelo, como o vento, fica de fora). */
const SHARED = ["position", "color", "tint"] as const;
/** Uniforms que cada vaga tem como seus; os outros vêm do material do lote. */
const OWN = new Set(["uDissolve", "uColor", "uDissolveEdge"]);
const edgeColor = new Color();

/**
 * Pool fixo de malhas que se desfazem (F95). Cada vaga é um `SkinnedMesh` com um osso preso a ela (a
 * matriz da peça vai na malha; o osso fica na identidade) e uma variante própria do dissolve do avatar
 * (o limiar e a cor são dela). A geometria é uma vista do modelo: posição, cor (a da paleta, se houver)
 * e a máscara `tint` divididas, sem a normal (o programa é o das malhas sem normal), e o skin de um osso.
 * As vistas ficam guardadas por modelo; o quadro não aloca.
 */
export class DissolvePool {
  /** "Reduzir lampejos": a borda acesa é efeito; com a opção, o pontilhado sem borda (`uDissolve` negativo, F55). */
  reduceFlashes = false;
  private readonly slots: Slot[] = [];
  private readonly views = new Map<string, { geometry: BufferGeometry; users: number }>();
  /**
   * Geometria da vaga parada: um triângulo vazio com o formato das vistas (posição, cor RGB e o skin,
   * sem normal). A pré-compilação compila também o que está invisível na cena (`compileAsync` percorre
   * tudo): sem posição nem cor, a vaga parada pedia uma variante a mais do programa do dissolve. E o
   * programa de uma vaga fica preso ao da primeira compilação dela: o three r186 só volta a escolher o
   * programa por alguns campos (`needsProgramChange`: skinning, lote, cor com alfa, morphs…), não pela
   * posição nem pela normal. A vaga que nascesse com outra forma desenharia as vistas com aquela variante.
   * Por isso a vaga parada já tem a forma das vistas.
   */
  private readonly empty = idleGeometry();
  private started = 0;
  private skipped = 0;

  constructor(root: Object3D, atmosphere: AtmosphereUniforms, size: number = DISSOLVE.slots) {
    // A mesma fábrica da pré-compilação (`toonDissolveMaterial(avatarMaterial(…))`): a mesma fonte e defines.
    const base = avatarMaterial(atmosphere);
    for (let i = 0; i < size; i++) {
      const material = toonDissolveMaterial(base);
      const color = { value: new Color(1, 1, 1) };
      material.uniforms["uColor"] = color;
      // A borda é da vaga (a fábrica a liga ao acento do avatar, que não pode mudar por ela).
      const edge = { value: new Vector4(1, 1, 1, 0) };
      material.uniforms["uDissolveEdge"] = edge;
      const mesh = new SkinnedMesh(this.empty, material);
      const bone = new Bone();
      mesh.add(bone);
      mesh.bind(new Skeleton([bone]));
      mesh.matrixAutoUpdate = false;
      // A peça está em qualquer lugar do mundo e a esfera do skin custaria uma conta por vértice: no máximo 8.
      mesh.frustumCulled = false;
      mesh.visible = false;
      mesh.name = "dissolve";
      root.add(mesh);
      this.slots.push({ mesh, material, dissolve: material.uniforms["uDissolve"] as { value: number }, color, edge, dither: false, start: 0, busy: false });
    }
  }

  /**
   * Começa o dissolve das peças, todas ou nenhuma (sem vagas para todas, devolve falso e a peça some como
   * antes). Quem chama tira a peça do lote no mesmo quadro: a cópia começa inteira, no mesmo lugar e cor.
   * Só o que o próprio jogador causa (`own`) usa as `DISSOLVE.reserved` últimas vagas.
   */
  start(parts: readonly DissolvePart[], now: number, own = false): boolean {
    if (parts.length === 0) return false;
    let free = 0;
    for (const s of this.slots) if (!s.busy) free++;
    if (free < parts.length + (own ? 0 : DISSOLVE.reserved)) {
      this.skipped++;
      return false;
    }
    let k = 0;
    for (const part of parts) {
      while (this.slots[k]!.busy) k++;
      const slot = this.slots[k]!;
      const uniforms = slot.material.uniforms;
      for (const name in part.material.uniforms) if (!OWN.has(name)) uniforms[name] = part.material.uniforms[name]!;
      const tint = part.material.uniforms["uColor"]?.value as Color | undefined;
      slot.color.value.set(1, 1, 1);
      if (tint) slot.color.value.copy(tint);
      if (part.color) slot.color.value.multiply(part.color);
      const accent = part.material.uniforms["uStyleAccent"]?.value as Vector4 | undefined;
      if (part.edge !== undefined) edgeColor.set(part.edge);
      else if (accent) edgeColor.setRGB(accent.x, accent.y, accent.z);
      else edgeColor.setRGB(1, 1, 1);
      slot.edge.value.set(edgeColor.r, edgeColor.g, edgeColor.b, 0);
      slot.dither = part.dither === true;
      slot.dissolve.value = 0;
      slot.mesh.geometry = this.view(part.geometry, part.colorAttribute ?? "color");
      slot.mesh.matrix.copy(part.matrix);
      slot.mesh.updateMatrixWorld(true);
      slot.mesh.visible = true;
      slot.start = now;
      slot.busy = true;
    }
    this.started++;
    return true;
  }

  /** Avança o limiar (0 inteiro, 1 sumido); a vaga que termina fica livre. */
  update(now: number): void {
    for (const slot of this.slots) {
      if (!slot.busy) continue;
      const k = (now - slot.start) / DISSOLVE.ms;
      if (k >= 1) {
        this.release(slot);
        continue;
      }
      const amount = Math.max(0, k);
      slot.dissolve.value = this.reduceFlashes || slot.dither ? -amount : amount;
    }
  }

  get active(): number {
    let n = 0;
    for (const s of this.slots) if (s.busy) n++;
    return n;
  }

  /** As malhas das vagas (para os testes e a vitrine). */
  get meshes(): readonly SkinnedMesh[] {
    return this.slots.map((s) => s.mesh);
  }

  stats(): DissolveStats {
    return { active: this.active, started: this.started, skipped: this.skipped };
  }

  dispose(): void {
    for (const slot of this.slots) {
      slot.mesh.removeFromParent();
      slot.mesh.skeleton.dispose();
      slot.material.dispose();
    }
    this.slots.length = 0;
    for (const { geometry } of this.views.values()) releaseView(geometry);
    this.views.clear();
    this.empty.dispose();
  }

  private release(slot: Slot): void {
    slot.busy = false;
    slot.mesh.visible = false;
    const entry = this.views.get(viewKey(slot.mesh.geometry));
    if (entry) entry.users--;
    slot.mesh.geometry = this.empty;
  }

  /** A vista de dissolve do modelo com aquele atributo de cor, guardada; acima do teto, sai a mais antiga sem uso. */
  private view(source: BufferGeometry, colorAttribute: string): BufferGeometry {
    const key = `${source.uuid}|${colorAttribute}`;
    let entry = this.views.get(key);
    if (entry) {
      // Vai para o fim (a mais recente).
      this.views.delete(key);
      this.views.set(key, entry);
    } else {
      entry = { geometry: dissolveView(source, colorAttribute), users: 0 };
      entry.geometry.userData["dissolveKey"] = key;
      this.views.set(key, entry);
      for (const [k, e] of this.views) {
        if (this.views.size <= DISSOLVE.views) break;
        if (e.users > 0 || e === entry) continue;
        releaseView(e.geometry);
        this.views.delete(k);
      }
    }
    entry.users++;
    return entry.geometry;
  }
}

function idleGeometry(): BufferGeometry {
  const source = new BufferGeometry();
  source.setAttribute("position", new BufferAttribute(new Float32Array(9), 3));
  source.setAttribute("color", new BufferAttribute(new Float32Array(9).fill(1), 3));
  return dissolveView(source);
}

const viewKey = (geometry: BufferGeometry): string => (geometry.userData["dissolveKey"] as string | undefined) ?? "";

/**
 * Vista de dissolve de um modelo (F95): divide a posição, a cor (`colorAttribute`, que vira `color`) e a
 * máscara `tint` com ele, e o índice, se houver; sem a normal e o vento; com o skin de um osso (índice 0,
 * peso 1). A cor fica RGB, como a do modelo: com alfa seria outro programa (`vertexAlphas`).
 */
export function dissolveView(source: BufferGeometry, colorAttribute = "color"): BufferGeometry {
  const view = new BufferGeometry();
  const position = source.getAttribute("position");
  view.setAttribute("position", position);
  const color = source.getAttribute(colorAttribute) ?? source.getAttribute("color");
  if (color) view.setAttribute("color", color);
  const tint = source.getAttribute("tint");
  if (tint) view.setAttribute("tint", tint);
  view.setIndex(source.index);
  const n = position.count;
  view.setAttribute("skinIndex", new BufferAttribute(new Uint8Array(n * 4), 4));
  const weight = new Uint8Array(n * 4);
  for (let i = 0; i < n; i++) weight[i * 4] = 255;
  view.setAttribute("skinWeight", new BufferAttribute(weight, 4, true));
  return view;
}

/**
 * Libera a vista sem tocar nos buffers do modelo: o `dispose` do three apaga da GPU todo atributo da
 * geometria, então os divididos saem dela antes, e só o skin vai embora.
 */
function releaseView(view: BufferGeometry): void {
  for (const name of SHARED) view.deleteAttribute(name);
  view.setIndex(null);
  view.dispose();
}
